import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, SecurityContext, signal, ViewEncapsulation } from '@angular/core';
import { translate, TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../../@vex/services/layout.service';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { CustomPoliciesService, CustomPolicy } from '@eadsysteme/portalsettingclient-v1';
import { DomSanitizer } from '@angular/platform-browser';
import { FormatUtil } from '../../services/FormatUtil';
import { SnackbarService } from '../../services/snackbar.service';
import { MatIcon } from '@angular/material/icon';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLabel } from '@angular/material/input';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SessionService } from '../../services/session.service';
import { AraButtonComponent } from '../../shared/ui/ara-button/ara-button.component';
import { AraDialogHeaderComponent } from '../../shared/ui/ara-dialog-header/ara-dialog-header.component';

@Component({
	selector: 'ara-policy-dialog',
	templateUrl: './policy-dialog.component.html',
	styleUrl: './policy-dialog.component.scss',
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatIcon,
		MatDialogTitle,
		CdkDrag,
		CdkDragHandle,
		MatDialogContent,
		MatDialogActions,
		MatCheckbox,
		ReactiveFormsModule,
		FormsModule,
		MatLabel,
		AraButtonComponent,
		AraDialogHeaderComponent,
	],
})
export class PolicyDialogComponent implements OnInit {
	public dialogRef: MatDialogRef<PolicyDialogComponent> = inject(MatDialogRef);
	private readonly domSanitizer: DomSanitizer = inject(DomSanitizer);
	private readonly layoutService: LayoutService = inject(LayoutService);
	public customPoliciesService: CustomPoliciesService = inject(CustomPoliciesService);
	private readonly snackBar: SnackbarService = inject(SnackbarService);
	private readonly sessionService: SessionService = inject(SessionService);

	isMobile = this.layoutService.isMobile() ?? false;

	protected dialogPolicyNaming = 'privacyAndTerms';

	hasPrivacyPolicy: boolean = false;
	hasTermsOfService: boolean = false;
	privacyPolicyHasCheckbox: boolean = true;
	termsOfServiceHasCheckbox: boolean = true;
	privacyPolicyChecked: boolean = false;
	termsOfServiceChecked: boolean = false;

	@Input() consentMode: boolean = true;
	@Input() logoutMode: boolean = false;

	@Input() public policies: CustomPolicy[];

	private readonly serviceCompanyName = signal<string | null>(null);
	protected readonly isSaving = signal(false);
	@Input() serviceCompanyArea: number;

	protected readonly subTitleText = computed(() => {
		const serviceCompanyName = this.serviceCompanyName();
		if (serviceCompanyName === null) {
			return '';
		}
		return serviceCompanyName
			? translate('policy.subTitleName', { name: serviceCompanyName, area: this.serviceCompanyArea })
			: translate('policy.subTitle', { area: this.serviceCompanyArea });
	});

	ngOnInit() {
		for (const policy of this.policies) {
			this.hasPrivacyPolicy = this.hasPrivacyPolicy || policy.type === 'PRIVACY_POLICY';
			this.hasTermsOfService = this.hasTermsOfService || policy.type === 'TERMS_OF_SERVICE';
		}

		if (this.consentMode) {
			if (!this.hasPrivacyPolicy) {
				this.privacyPolicyChecked = true;
				this.privacyPolicyHasCheckbox = false;
			}
			if (!this.hasTermsOfService) {
				this.termsOfServiceChecked = true;
				this.termsOfServiceHasCheckbox = false;
			}
		} else {
			this.privacyPolicyChecked = true;
			this.privacyPolicyHasCheckbox = false;
			this.termsOfServiceChecked = true;
			this.termsOfServiceHasCheckbox = false;
		}

		this.setDialogPolicyNaming();
		this.loadServiceCompanyName();
	}

	consent() {
		if (this.isSaving()) {
			return;
		}

		if (!this.policies?.length) {
			this.dialogRef.close({ consented: true, dialogType: this.dialogPolicyNaming });
			return;
		}

		this.isSaving.set(true);
		forkJoin(this.policies.map((policy) => this.customPoliciesService.consentToCustomPolicy(0, policy.policyId)))
			.pipe(finalize(() => this.isSaving.set(false)))
			.subscribe({
				next: () => this.dialogRef.close({ consented: true, dialogType: this.dialogPolicyNaming }),
				error: () => this.snackBar.error(translate('policy.error')),
			});
	}

	decline() {
		this.dialogRef.close({ consented: false, dialogType: this.dialogPolicyNaming });
	}

	private setDialogPolicyNaming() {
		if (this.hasPrivacyPolicy && !this.hasTermsOfService) {
			this.dialogPolicyNaming = 'PRIVACY_POLICY';
		}
		if (this.hasTermsOfService && !this.hasPrivacyPolicy) {
			this.dialogPolicyNaming = 'TERMS_OF_SERVICE';
		}
	}

	private loadServiceCompanyName() {
		// Fehler und fehlende Namen fängt der Service selbst ab und liefert '' — hier reicht der next-Zweig.
		this.sessionService.getServiceCompanyName(this.serviceCompanyArea).subscribe((serviceCompanyName) => this.serviceCompanyName.set(serviceCompanyName));
	}

	protected sanitizeHtmlContent(htmlContent: string) {
		htmlContent = FormatUtil.stripWhitespace(htmlContent);
		return this.domSanitizer.sanitize(SecurityContext.HTML, htmlContent);
	}
}
